// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE WORK PROPOSAL — Air Duct and Everything 420, as actual A4 documents.
//
// THE DESIGN IS THE MAX SECURITY PROPOSAL, PORTED. Not "inspired by" — the same
// palette, the same blocks, the same order, lifted from the CSS in
// scripts/generate-proposals.mjs, which is the only proposal we have ever sent:
//
//   header (logo left / meta right) → gradient rule → title →
//   client box → scope (one paragraph + the list it contains) → output note →
//   phases in coloured pillar bands → deliverables (2-up, check discs) →
//   summary box (gradient) → terms.
//
// TWO DELIBERATE DEPARTURES FROM MAX, both forced:
//   1. LTR, not RTL. MAX was Hebrew; these two clients are American. That also
//      means the accent rule sits on the LEFT edge of the client box, and the
//      timeline gradient runs left-to-right. Mixing directions is how a header
//      silently scrambles, so there is exactly one direction on this page.
//   2. NO PER-LINE PRICES, same as MAX, which quoted one flat number. Itemising
//      the price invites a line-by-line negotiation on the cheapest rows and
//      turns the conversation into arithmetic. The total is still DERIVED from
//      the shared rate card; the derivation lives in the calculator, which is
//      ours, not theirs.
//
// Sheet is A4 at 96dpi (794px) with the same 16mm/12mm margins page.pdf() uses,
// so what is on the board is what prints.
//
// THE RATE CARD MIRRORS `DEFAULT_KNOBS` in BOTH calculators (everything420/
// pricing.tsx, airduct/calculator.tsx — identical). Re-declared, not imported:
// those files belong to Ruby and Hank, and my board must not move when they
// tune a knob mid-review. If these ever diverge, that is a bug — say it aloud.
//
// Owner agent: Jarvis.
// ─────────────────────────────────────────────────────────────────────────────

import { Fragment } from "react";

/** MAX's palette, verbatim — matches the dashboard ArchitectureBlocks tokens. */
const T = {
  bg: "#F2F7FD",
  bg2: "#E5EEF9",
  ink: "#1C1917",
  ink2: "#57534E",
  ink3: "#A8A29E",
  sky: "#7AB4F4",
  skyDark: "#3B82C8",
  skySoft: "#D2E4FC",
  line: "#D0DEF0",
  accent: "#2A6AAC",
};

const FONT = 'Heebo, Inter, ui-sans-serif, -apple-system, "Segoe UI", Arial, sans-serif';

/** A4 at 96dpi, with page.pdf()'s own margins (16mm sides, 12mm top/bottom). */
const SHEET_W = 794;
/** A4 height at 96dpi. Pages are FIXED height on purpose: if content overflows,
 *  it spills visibly past the sheet on the board — the warning you want before
 *  it silently becomes a third page in the PDF. */
const SHEET_H = 1123;
const MARGIN_X = 60;
const MARGIN_Y = 40;

/** Mirrors DEFAULT_KNOBS in both calculators. One rate, every client. */
const RATE = {
  S: 1200, M: 2000, L: 4500,
  // Bands flattened from 1.25/1.5/1.75. The old spread punished Air Duct twice:
  // it has four large features AND more users on most of them, so the multiplier
  // compounded exactly where the scope was already heaviest.
  b1: 1.0, b2: 1.1, b3: 1.2, b4: 1.3,
  // Setup is ONE flat number now — Erez, 2026-07-29. It used to be discovery +
  // operating system + a data layer + six connectors, which is a build sheet,
  // not a price. The client buys "the system, set up".
  setup: 5000,
  retainer: 2500,
};

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

function band(users: number | null): number {
  if (users == null) return 0;
  if (users <= 2) return RATE.b1;
  if (users <= 10) return RATE.b2;
  if (users <= 30) return RATE.b3;
  return RATE.b4;
}

type Line = { name: string; size: "S" | "M" | "L" | "?"; users: number | null };

function priceOf(l: Line): number | null {
  const base = l.size === "S" ? RATE.S : l.size === "M" ? RATE.M : l.size === "L" ? RATE.L : null;
  return base == null ? null : base * band(l.users);
}

// ─── The two engagements ─────────────────────────────────────────────────────

type Proposal = {
  number: string;
  client: string;
  clientSub: string;
  title: string;
  /** Per-client. Air Duct is two months of work; 420 phase one is four. */
  pillarWeeks: [string, string, string];
  durationTerm: string;
  /** The duration, said short. The price band that used it is gone; the terms
   *  line still needs it, and one value keeps the two from disagreeing. */
  durationShort: string;
  /** Where the sheet ends. Air Duct has ten scope lines so it breaks after the
   *  scope; 420 has three, so its first page carries the work process too. */
  breakAfter: "scope" | "process" | "receive" | "total" | "terms";
  /** THE QUOTED PRICE. Set by hand, because a price is a commercial decision
   *  rather than the output of a calculation. The rate card still DERIVES a
   *  number and the calculator on the board still shows it — this is what we
   *  actually ask for, and the gap between the two is ours to know, not theirs. */
  price: number;
  dataLayer: { label: string; amount: number; why: string };
  sold: Line[];
  later: Line[];
  laterNote: string;
  /** One paragraph. Three icon cards fragmented the read — the client wants to
   *  know what this IS in one breath, not scan a grid. */
  scope: string;
  /** The scope list, consolidated INTO the scope section (there is no separate
   *  "what is included" table any more — they were the same thing said twice).
   *  Titles and descriptions are the CLIENT'S OWN confirmed wording where we
   *  have it, never my paraphrase of it. */
  included: { title: string; body: string; flag?: string }[];
  deliverables: string[];
};

const AIR_DUCT: Proposal = {
  // TM-<client id>-<revision>. The four digits identify the DEAL and never
  // change; the trailing number is which proposal this is for that deal, so a
  // re-quote after they push back is TM-4827-2. Fixed, not generated — a number
  // that moves on reload looks broken, and a client may quote it back to us.
  number: "TM-4827-1",
  // THEIR name, not our folder key. "Air Duct TX" is the internal slug and it
  // had walked onto the client-facing document. Trading name confirmed off
  // bestairducts.com (Hank, 27 Jul; re-fetched live 29 Jul).
  // ⚠️ Still the TRADING name — the registered legal entity is unconfirmed and
  // is what a signature block actually needs. Open with Matan.
  client: "Fresh Air Duct Cleaning",
  clientSub: "Dallas, Texas",
  title: "Work proposal",
  pillarWeeks: ["Weeks 1-3", "Weeks 3-7", "Week 8"],
  durationTerm: "Duration: 2 months from signature to handover",
  durationShort: "2 months",
  breakAfter: "process",
  price: 28500,   // derived from the rate card: 29,950
  dataLayer: {
    label: "Warehouse",
    amount: 4000,
    why: "Built from nothing — none of the source systems are modelled today.",
  },
  // John confirmed 10/10 yes; this quote carries seven of them. Running late
  // and permissions-and-a-safety-gate are cut outright (Erez, 2026-07-29) —
  // a deferred line on a proposal reads as filler, so it does not appear at
  // all. Call visibility stays listed but flagged: it cannot be sized until
  // CallRail is opened.
  sold: [
    { name: "Definitions catalogue", size: "M", users: 4 },
    { name: "Real-time automated reporting", size: "M", users: 6 },
    { name: "Ask in plain language", size: "L", users: 3 },
    { name: "Schema-drift watch and repair", size: "S", users: 0 },
    { name: "Speed-to-lead, in-house", size: "L", users: 0 },
    { name: "Capacity model and schedule solver", size: "L", users: 5 },
    { name: "The commission run", size: "L", users: 3 },
  ],
  later: [],
  laterNote: "",
  scope: "One place your operational data actually lives, and the workflows that run on top of it. Built over two months and handed over working, with your team trained to run it.",
  included: [
    { title: "Discovery and analysis",
      body: "Your processes mapped end to end, with the people who run them." },
    { title: "Setting up the operating system",
      body: "Connecting to your third-party systems and your data, on a server you own." },
    { title: "Definitions catalogue",
      body: "One definition of a job, a lead, a source. Two reports stop disagreeing." },
    { title: "Real-time automated reporting",
      body: "By market, channel and service. Period and cohort kept apart." },
    { title: "Ask questions in plain language",
      body: "Answers with the numbers behind them." },
    { title: "Self-healing data connections",
      body: "A renamed field is detected, repaired, and reported." },
    { title: "Speed to lead, in-house",
      body: "New leads contacted immediately and routed. Replaces what you use today." },
    { title: "Scheduling and capacity",
      body: "Real crew availability. No double-booking, no slot that does not exist." },
    { title: "Commissions and payroll",
      body: "The cycle calculated, exceptions flagged, approved in one place." },
    { title: "Call centre visibility",
      body: "Handle time, abandoned calls, answer rates.",
      flag: "Scoped once we see what your phone system exposes" },
  ],
  deliverables: [
    "Full process map, visual",
    "The system deployed on a server you own",
    "Training documentation for the team",
  ],
};

/** PHASE ONE — the answer to Daniel's price pushback (1 Aug 2026).
 *
 *  He replied to TM-4827-1 with "the pricing seems very expensive… perhaps we
 *  are taking on too large a project before testing some results". That is not
 *  a rejection, it is the client naming the fix himself, so this is the same
 *  deal at revision 2 rather than a discount: the full programme is untouched
 *  and still on the table as TM-4827-1.
 *
 *  Four lines only — the foundation plus the two things he can actually judge.
 *  Priced at 8,500 to MATCH Everything 420, because both are setup plus about
 *  one use case. One rate, one shape, two clients. */
const AIR_DUCT_P1: Proposal = {
  number: "TM-4827-2",
  client: "Fresh Air Duct Cleaning",
  clientSub: "Dallas, Texas",
  title: "Work proposal",
  pillarWeeks: ["Weeks 1-2", "Weeks 2-3", "Week 4"],
  durationTerm: "Duration: 4 weeks from signature to handover",
  durationShort: "4 weeks",
  breakAfter: "total",
  price: 8500,
  dataLayer: {
    label: "Warehouse",
    amount: 4000,
    why: "Built from nothing — none of the source systems are modelled today.",
  },
  sold: [
    { name: "Real-time automated reporting", size: "M", users: 6 },
    { name: "Ask questions in plain language", size: "L", users: 3 },
  ],
  later: [],
  laterNote: "",
  scope: "A first phase: the system set up, connected to your data, and two things you can use straight away. Built over four weeks, so you can see it working before deciding what comes next.",
  included: [
    { title: "Discovery and analysis",
      body: "Working sessions with the people who run the process, mapped end to end, down to the detail." },
    { title: "Setting up the system",
      body: "Connecting to your third-party systems and your data, on a server you own." },
    { title: "Real-time automated reporting",
      body: "By market, channel and service. Period and cohort kept apart." },
    { title: "Ask questions in plain language",
      body: "Answers with the numbers behind them." },
  ],
  deliverables: [
    "Full process map, visual",
    "The system deployed on a server you own",
    "Training documentation for the team",
  ],
};

const ALL_420: Proposal = {
  number: "TM-7391-1",
  client: "Everything 420",
  clientSub: "United States",
  title: "Work proposal",
  pillarWeeks: ["Weeks 1-2", "Weeks 2-3", "Week 4"],
  durationTerm: "Duration: 4 weeks from signature to handover",
  durationShort: "4 weeks",
  breakAfter: "total",
  price: 8500,    // derived from the rate card: 9,500
  dataLayer: {
    label: "Data layer",
    amount: 3000,
    why: "Lower than comparable engagements on this one line: a large part of the plumbing already exists on your box.",
  },
  sold: [{ name: "Product image pipeline", size: "L", users: 2 }],
  later: [
    { name: "Purchasing and purchase orders", size: "L", users: 3 },
    { name: "Ask the business a question", size: "L", users: 5 },
    { name: "Customer support agent", size: "M", users: 4 },
    { name: "Landing page and CRO testing", size: "M", users: 3 },
    { name: "Shipping-label failure watch", size: "M", users: 0 },
    { name: "Marketing performance agent", size: "M", users: 3 },
    { name: "Vendor and product analysis", size: "M", users: 3 },
    { name: "Sheets to store bridge", size: "M", users: 4 },
    { name: "Bulk product operations", size: "M", users: 2 },
    { name: "Run management and guardrails", size: "M", users: 2 },
    { name: "State shippability matrix", size: "M", users: 3 },
    { name: "Listing generation from intake", size: "M", users: 2 },
    { name: "Wholesale sheet sync", size: "S", users: 2 },
  ],
  laterNote: "Store-platform migration assist stays unsized on purpose — it depends entirely on what your team keeps.",
  scope: "Phase one: the setup, and the product image pipeline you asked for first. Built over four weeks and handed over working.",
  included: [
    { title: "Discovery and analysis",
      body: "How your catalogue moves today, mapped end to end." },
    { title: "Setting up the operating system",
      body: "Connected to your store and supplier intake, on a server you own." },
    { title: "The product image pipeline",
      body: "Supplier photos in, publishable product pages out. One image at a time, to a written standard." },
  ],
  deliverables: [
    "Full process map, visual",
    "The system deployed on a server you own",
    "Training documentation for the team",
  ],
};

// ─── Phases — MAX's four pillars, same shape ─────────────────────────────────

// Three stages, not four. Analysis and Setup were one stage pretending to be
// two, and every line that was reassurance rather than information is gone —
// candidate ranking and "you choose" (the scope is already confirmed), the
// re-interview in Build (that IS stage one), the weekly check-in, and the
// operating-cost sentence. If a line does not change what the client knows or
// decides, it is not on the page.
const PILLARS = [
  {
    roman: "I", name: "Discovery and system setup", color: T.accent,
    phases: [
      { n: 1, title: "Process analysis",
        what: "Working sessions with the people who run the process, mapped end to end, down to the detail." },
      { n: 2, title: "Setting up the AI operating system",
        what: "Connected to all your systems." },
    ],
  },
  {
    roman: "II", name: "Build", color: T.skyDark,
    phases: [
      { n: 3, title: "Build",
        what: "Iterative, with internal testing throughout." },
      { n: 4, title: "Review and polish",
        what: "Walk-through with the team who will use it. Bug fixes, acceptance criteria verified, shadow mode before anything runs for real." },
    ],
  },
  {
    roman: "III", name: "Operate", color: T.accent,
    phases: [
      { n: 5, title: "Training, runbook and tracking",
        what: "We train the team to run it. KPIs tracked. Formal handover." },
    ],
  },
];

const TERMS = [
  "Payment: 50% upfront, 50% on delivery",
  "This proposal is valid for 30 days from its issue date",
  "Price excludes tax",
  "Full ownership of all code and artifacts — 100% yours",
  "Any change to the scope above is priced and agreed in writing before that work begins",
  "The timeline depends on your availability. Internal delays on your side move the dates accordingly, and we will tell you immediately",
  "You carry the token usage cost — the models bill per use (Anthropic / AWS). Keeping that cost down is part of the job: we measure it, and we work with you to lower it by trying different models and different methods",
];

// ─── Document primitives — one per MAX CSS block ─────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6, margin: "13px 0 6px",
      fontSize: 11, fontWeight: 700, color: T.accent,
      textTransform: "uppercase", letterSpacing: "1px",
    }}>
      {children}
      <span style={{ flex: 1, height: 1, background: T.line }} />
    </div>
  );
}

function Divider() {
  return <div style={{ height: 2, borderRadius: 2, margin: "7px 0", background: `linear-gradient(to right, ${T.skyDark}, ${T.accent})` }} />;
}

// ─── The document ────────────────────────────────────────────────────────────

/** ONE A4 SHEET. `data-page` is what the PDF shell targets to drop the
 *  on-screen gap between sheets. */
function Page({ children, last }: { children: React.ReactNode; last?: boolean }) {
  return (
    <div
      data-page
      style={{
        width: SHEET_W, minHeight: SHEET_H, background: "#fff",
        padding: `${MARGIN_Y}px ${MARGIN_X}px`,
        fontFamily: FONT, color: T.ink, fontSize: 13, lineHeight: 1.6,
        border: `1px solid ${T.line}`, direction: "ltr", textAlign: "left",
        marginBottom: last ? 0 : 28,
        breakAfter: last ? "auto" : "page",
      }}
    >
      {children}
    </div>
  );
}

export function ProposalSheet({ p }: { p: Proposal }) {
  // The total is still DERIVED from the shared rate card — it is only the
  // per-line breakdown that stays off the client-facing page.
  // Derived from the shared rate card — kept as the sanity check on the quoted
  // figure, and because the calculator on the board shows the same arithmetic.
  const derived = RATE.setup + p.sold.reduce((s, l) => s + (priceOf(l) ?? 0), 0);
  const quoted = p.price ?? derived;

  const head = (
    <>
        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.accent, letterSpacing: "-0.5px" }}>Tailormind</div>
            <div style={{ fontSize: 10, color: T.ink3, marginTop: 1 }}>AI Consulting</div>
          </div>
          <div style={{ textAlign: "right", fontSize: 11, color: T.ink2, lineHeight: 1.8 }}>
            <div>Proposal no.: <strong style={{ color: T.accent, fontWeight: 600 }}>{p.number}</strong></div>
            <div>Date: <strong style={{ color: T.accent, fontWeight: 600 }}>30 July 2026</strong></div>
          </div>
        </div>
  
        <Divider />
  
        {/* ── Doc title ── */}
        <div style={{ margin: "9px 0" }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.accent, lineHeight: 1.25 }}>{p.title}</h1>
        </div>
  
        {/* ── Client box ── */}
        <div style={{
          background: T.bg, borderLeft: `4px solid ${T.accent}`, borderRadius: 6,
          padding: "9px 14px", margin: "12px 0", display: "flex", gap: 48,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{p.client}</div>
            <div style={{ fontSize: 11, color: T.ink2 }}>{p.clientSub}</div>
          </div>
        </div>
  
    </>
  );

  const scope = (
    <>
        {/* ── Scope — HIGH LEVEL ONLY ──────────────────────────────────────────
            Erez, 2026-07-29: the scope must NOT get into detail. One paragraph
            that says what the project is, then the items it contains as a plain
            list. The per-item explanations are gone — they belonged to the
            confirmation email, where the client was being asked to check each
            line. A proposal is not that document; detail here invites the reader
            to negotiate the parts instead of accepting the whole. */}
        <SectionTitle>Scope</SectionTitle>
        <p style={{ fontSize: 12, color: T.ink2, lineHeight: 1.6, marginBottom: 10 }}>{p.scope}</p>
        <div>
          {p.included.map((it, i) => (
            <div key={it.title} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 6 }}>
              <span style={{
                minWidth: 17, height: 17, borderRadius: "50%", background: it.flag ? T.sky : T.accent,
                color: "#fff", fontSize: 9.5, fontWeight: 700, display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, marginTop: 1,
              }}>{i + 1}</span>
              <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                <span style={{ fontWeight: 600 }}>{it.title}</span>
                {it.flag && (
                  <span style={{
                    marginLeft: 7, padding: "1px 7px", borderRadius: 20, background: T.skySoft,
                    color: T.accent, fontSize: 9, fontWeight: 600, letterSpacing: ".2px",
                    textTransform: "uppercase", whiteSpace: "nowrap",
                  }}>{it.flag}</span>
                )}
                <div style={{ fontSize: 10.5, color: T.ink2, lineHeight: 1.4, marginTop: 1 }}>{it.body}</div>
              </div>
            </div>
          ))}
        </div>
  
    </>
  );

  const process = (
    <>
        {/* ── Phases ── */}
        <SectionTitle>The work process</SectionTitle>
        {PILLARS.map((pil, i) => (
          <div key={pil.roman} style={{ marginTop: 8 }}>
            <div style={{
              background: pil.color, color: "#fff", fontSize: 10, fontWeight: 700,
              letterSpacing: ".6px", padding: "3px 10px", borderRadius: "3px 3px 0 0", textTransform: "uppercase",
            }}>{pil.roman} · {pil.name} <span style={{ opacity: 0.75, fontWeight: 600 }}>· {p.pillarWeeks[i]}</span></div>
            {pil.phases.map((ph, i) => (
              <div key={ph.n} style={{
                display: "flex", gap: 8, alignItems: "flex-start", padding: "4px 10px",
                background: T.bg, borderLeft: `2px solid ${T.line}`, borderBottom: `1px solid ${T.bg2}`,
                borderRadius: i === pil.phases.length - 1 ? "0 0 3px 3px" : undefined,
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", background: pil.color, color: "#fff",
                  fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0, marginTop: 1,
                }}>{ph.n}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600 }}>
                    {ph.title}
                  </div>
                  <div style={{ fontSize: 10.5, color: T.ink2, lineHeight: 1.4 }}>{ph.what}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
  
    </>
  );

  const receive = (
    <>
        {/* ── Deliverables ── */}
        <SectionTitle>What you receive</SectionTitle>
        <div style={{ display: "grid", gap: 5 }}>
          {p.deliverables.map((d) => (
            <div key={d} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 10.5, lineHeight: 1.35 }}>
              <span style={{
                width: 13, height: 13, borderRadius: "50%", background: T.accent, color: "#fff",
                fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, marginTop: 1,
              }}>✓</span>
              <span>{d}</span>
            </div>
          ))}
        </div>
  
    </>
  );

  const total = (
    <>
        {/* ── Summary box — MAX's gradient price band ── */}
        <div style={{
          background: `linear-gradient(135deg, ${T.accent} 0%, ${T.skyDark} 100%)`,
          borderRadius: 8, padding: "16px 20px", margin: "14px 0 12px",
          display: "flex", justifyContent: "space-between", alignItems: "center", color: "#fff",
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".8px", textTransform: "uppercase", opacity: 0.8 }}>
            Total investment
          </div>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-1px" }}>{usd(quoted)}</div>
        </div>
  
    </>
  );

  const terms = (
    <>
        {/* ── Terms ── */}
        <SectionTitle>Terms</SectionTitle>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 10.5, color: T.ink2, lineHeight: 1.7 }}>
          {[TERMS[0], TERMS[1], TERMS[2], TERMS[3], TERMS[4], p.durationTerm, ...TERMS.slice(5)].map((t) => <li key={t}>{t}</li>)}
        </ul>
  
    </>
  );

  const acceptance = (
    <>
        {/* ── Signature ──────────────────────────────────────────────────────────
            The document used to stop on the terms, which left the reader to
            invent the next step. A signature block IS the close: it says this is
            a thing you sign, and it says who signs it. */}
        <SectionTitle>Acceptance</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36, marginTop: 4 }}>
          {[{ party: "Tailormind", who: "Erez Fern, Founder & CEO" }, { party: p.client, who: "Name and title" }].map((c) => (
            <div key={c.party}>
              <div style={{ fontSize: 9, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: ".8px" }}>
                {c.party}
              </div>
              <div style={{ fontSize: 11, color: T.ink2, marginTop: 2 }}>{c.who}</div>
              <div style={{ borderBottom: `1px solid ${T.ink3}`, height: 34 }} />
              <div style={{ fontSize: 9, color: T.ink3, marginTop: 3 }}>Signature</div>
              <div style={{ borderBottom: `1px solid ${T.ink3}`, height: 26, marginTop: 10 }} />
              <div style={{ fontSize: 9, color: T.ink3, marginTop: 3 }}>Date</div>
            </div>
          ))}
        </div>
  
    </>
  );

  // WHERE THE SHEET ENDS. Both documents are two pages, so the job is to fill
  // page one and cut on a section boundary — never mid-section. `breakAfter`
  // names the LAST section on page one; everything after it goes to page two.
  const ORDER = [head, scope, process, receive, total, terms, acceptance];
  const KEYS = ["head", "scope", "process", "receive", "total", "terms", "acceptance"];
  const cut = KEYS.indexOf(p.breakAfter) + 1;

  return (
    <>
      <Page>{ORDER.slice(0, cut).map((s, i) => <Fragment key={i}>{s}</Fragment>)}</Page>
      <Page last>{ORDER.slice(cut).map((s, i) => <Fragment key={i}>{s}</Fragment>)}</Page>
    </>
  );
}

export function AirDuctPhaseOneProposal() {
  return <ProposalSheet p={AIR_DUCT_P1} />;
}

export function AirDuctProposal() {
  return <ProposalSheet p={AIR_DUCT} />;
}

export function Everything420Proposal() {
  return <ProposalSheet p={ALL_420} />;
}
